<template>
  <div :id="id" class="business">
    <div class="business__top">
      <div class="componata-container">
        <FancyHeading class="business__heading">
          <template v-slot:title><span v-html="content.title"></span></template>
        </FancyHeading>

        <Tabs
          :data="navigationTabs"
          @change="showTabContent($event)"
          id="business-features-nav"
          class="business__tabs"
        />

        <div class="business__content-area">
          <div
            v-for="(item, index) in content.sections"
            :key="index"
            class="business__content-section-wrapper"
            :class="{ '--is-visible' : activeContentIndex === index }"
          >
            <section class="business__content-section">
              <div class="business__content-text">
                <h2 class="business__content-title" v-html="item.title"></h2>
                <p class="business__content-description" v-html="item.description"></p>
              </div>

              <div class="business__content-media">
                <img
                  :srcset="`
                    ${item.media.url}?format=${webPFormat('png')}&width=1300&quality=80 1126w,
                    ${item.media.url}?format=${webPFormat('png')}&width=650&quality=80 563w
                  `"
                  :src="`${item.media.url}?format=${webPFormat('png')}&width=650&quality=80`"
                  :width="item.media.width / 2"
                  class="business__content-pic"
                >
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>

    <div class="business__bottom">
      <div class="componata-container">
        <ul class="business__feature-list">
          <li
            v-for="(item, index) in content.sections[activeContentIndex].features"
            :key="index"
            class="business__feature"
          >
            <span class="business__feature-icon" v-html="item.icon"></span>
            <b class="business__feature-title">{{ item.title }} <span v-if="item.new_flag" class="business__feature-badge">Neu!</span></b>
            <p class="business__feature-description" v-html="item.description"></p>
            <p
              v-if="item.more_info && item.more_info.link"
              class="business__feature-extra"
            >
              <a
                @click="modal = true; modalUrl = item.more_info.link"
                class="business__feature-info"
              >
                <i class="icon icon-info-circle"></i> {{ item.more_info.text }}
              </a>
            </p>
          </li>
        </ul>
      </div>
    </div>

    <ModalExternalContent
      v-if="modal"
      :url="modalUrl"
    />
  </div>
</template>

<script>
import { helperMixin } from '@/mixins/HelperMixin';
import { UiMixin } from '@/mixins/UiMixin';

import FancyHeading from '@/components/Pages/FancyHeading';
import Tabs from '@/components/Pages/Tabs';
import ModalExternalContent from '@/components/Pages/ModalExternalContent';

export default {
  name: 'BusinessFeatures',
  mixins: [helperMixin, UiMixin],
  components: {
    FancyHeading,
    Tabs,
    ModalExternalContent
  },
  props: {
    id: {
      type: String,
      default: 'business-features'
    },
    content: {
      type: Object
    }
  },
  data () {
    return {
      navigationTabs: [],
      activeContentIndex: 0,
      modal: false
    };
  },
  methods: {
    showTabContent ($event) {
      this.activeContentIndex = $event;

      const stickyNavHeight = document.querySelector('#sticky-nav').offsetHeight || 0;

      this.scrollTo('business-features-nav', null, null, (-stickyNavHeight) - 15);
    }
  },
  created () {
    // Create navigation tabs object based on the content
    this.content.sections.forEach(item => {
      this.navigationTabs.push({
        icon: item.tab_icon,
        title: item.tab_title
      });
    });
  },
  updated () {
    this.$nextTick(() => {
      this.$root.$emit('AOSRefresh');
    });
  }
};
</script>

<style lang="scss" scoped>
/**
 * Business Features
 *
 * @author juanpablob <m.juanpablob@gmail.com>
 * @desc Content section for showcasing the business features of Componata
 * @since 2022-06-29
**/

$component: 'business';

.#{$component} {
  @include padding-top(map-get($spacers, 6) * 2);
  background: $ink-lighter linear-gradient(to bottom, #fff, #f4f4f4);

  &__top {
    // background: $ink-lighter linear-gradient(to bottom, #fff, #f4f4f4);
  }

  &__heading {
    --from: #212B36;
    --to: #235994;
    --shadow: #ffffff01;

    width: 60%;
    margin: 0 auto;
    @include margin(0 auto (map-get($spacers, 5) * 2) auto);
    text-align: center;
  }

  &__tabs {
    --color-active: #235994;

    // @include margin-bottom(map-get($spacers, 6));
  }

  &__content-area {
    transition: all 0.3s ease-in-out;
  }

  &__content-section-wrapper {
    display: none;

    &.--is-visible {
      display: block;
    }
  }

  &__content-section {
    display: flex;
    width: 100%;
    min-height: 26rem;
    animation: content-in 0.6s ease-in-out forwards;

    @keyframes content-in {
      from {
        opacity: 0;
      }

      to {
        opacity: 1;
      }
    }
  }

  &__content-text {
    flex-basis: 40%;
    flex-shrink: 0;
    @include padding(map-get($spacers, 6) map-get($spacers, 4) map-get($spacers, 6) 0);
  }

  &__content-title {
    margin: 0;
    font-family: $font-family-serif;
    @include font-size(map-get($lp, 'font-size-xl'));
    font-weight: $font-weight-lighter;
  }

  &__content-description {
    @include margin(map-get($spacers, 4) 0 0 0);
    @include font-size(map-get($lp, 'font-size-lg'));
    font-weight: $font-weight-light;
  }

  &__content-media {
    flex-grow: 1;
    position: relative;
    overflow: hidden;
  }

  &__content-pic {
    @include imageRendering;
    width: 100%;
    position: absolute;
    top: 0;
    animation: swing-in 0.6s ease-in-out forwards;
    will-change: transform;

    @keyframes swing-in {
      from {
        transform: translateX(0.5rem);
      }

      to {
        transform: translateX(0);
      }
    }
  }

  &__bottom {
    @include padding-bottom(map-get($spacers, 6));
    // background: $ink linear-gradient(to bottom, $ink 80%, rgba(black, 0.2));
    // color: $sky-lighter;
    background-color: $white;
    color: $ink;

    .componata-container {
      position: relative;
      // border-top: 1px solid $sky;

      &:after {
        content: '';
        display: none;
        position: absolute;
        @include rfs(- 1.6rem, top);
        @include rfs(map-get($spacers, 5), left);
        width: 0px;
        height: 0px;
        border-style: solid;
        @include rfs(0 1.6rem 34.6px 1.6rem, border-width);
        border-radius: $border-radius-lg;
        border-color: transparent transparent $ink transparent;
      }
    }
  }

  &__feature-list {
    @include unlist;
    display: flex;
    flex-wrap: wrap;
    @include padding-top(map-get($spacers, 6));
  }

  &__feature {
    display: flex;
    flex-direction: column;
    flex-basis: 28%;
    @include margin-bottom(map-get($spacers, 6));

    &:not(:nth-of-type(3n+3)) {
      margin-right: 8%;
    }
  }

  &__feature-icon {
    @include rfs(3rem, width);
    min-width: 2rem;
    @include margin-bottom(map-get($spacers, 3));
    color: #235994;
  }

  &__feature-title {
    @include font-size(map-get($lp, 'font-size-lg'));
  }

  &__feature-description {
    @include margin(map-get($spacers, 2) 0 0 0);
    font-weight: $font-weight-light;
  }

  &__feature-extra {
    @include margin-top(map-get($spacers, 2));
  }

  &__feature-info {
    display: flex;
    align-items: center;

    .icon {
      @include margin-right(map-get($spacers, 1));
      @include font-size($font-size-base * 1.2);
    }
  }

  /** RESPONSIVENESS **/

  // ——————————————————————————————
  // Max LG
  // ——————————————————————————————
  @media (max-width: map-get($lp, 'viewport-lg')) {
  }

  // ——————————————————————————————
  // Max MD
  // ——————————————————————————————
  @media (max-width: map-get($lp, 'viewport-md')) {
    &__heading {
      width: 70%;
    }
  }

  // ——————————————————————————————
  // Max SM
  // ——————————————————————————————
  @media (max-width: map-get($lp, 'viewport-sm')) {
    &__heading {
      width: 80%;
    }

    &__content-section {
      flex-direction: column;
    }

    &__content-text {
      padding-right: 0;
    }

    &__content-pic {
      position: static;
    }

    &__feature-list {
    }

    &__feature {
      flex-basis: 48%;

      &:not(:nth-of-type(3n+3)) {
        margin-right: 0;
      }

      &:nth-last-of-type(2n+1) {
        margin-right: 4%;
      }
    }

    &__feature-icon {
      @include margin-bottom(map-get($spacers, 1));
    }
  }

  // ——————————————————————————————
  // Max XS
  // ——————————————————————————————
  @media (max-width: map-get($lp, 'viewport-xs')) {
    &__heading {
      width: 90%;
    }

    &__feature {
      flex-basis: 100%;
      align-items: center;
      text-align: center;

      &:not(:nth-of-type(3n+3)) {
        margin-right: 0;
      }

      &:nth-last-of-type(2n+1) {
        margin-right: 0;
      }
    }

    &__bottom {
      .componata-container:after {
        display: none;
      }
    }
  }
}
</style>
